import axios, { AxiosInstance, AxiosError } from 'axios';

import SecureStorage from 'utils/localStorage';
import { persistWebViewLoadingInformation } from 'utils/helpers';
import {
  errorToast, warnToast, infoToast, 
} from 'utils/toast';

import offlineClient, { IOfflineGet } from './offlineAgent';

const baseURL = import.meta.env.VITE_APP_BASE_URL;
const reactNativeWebView = (window as any)?.ReactNativeWebView;

// Error tracking to prevent duplicate toasts
const errorHistory = new Map<string, number>();
// 1 minute threshold for duplicate errors
const DUPLICATE_THRESHOLD = 60000;

function shouldShowToast(error: AxiosError, statusCode?: number): boolean {
  if (error.config?.url?.includes('/auth/me')) {
    return false;
  }

  // Don't show toast for authentication errors (handled by redirect)
  if (statusCode === 401) {
    return false; 
  }
  
  // Don't show toast for validation errors that should be handled by forms
  if (statusCode === 422) {
    return false; 
  }
  
  // Don't show toast for /auth/me endpoint errors
  
  // Check for duplicate errors
  const errorKey = `${statusCode}-${error.config?.url}`;
  const now = Date.now();
  const lastSeen = errorHistory.get(errorKey);
  
  if (lastSeen && (now - lastSeen) < DUPLICATE_THRESHOLD) {
    return false;
  }
  
  errorHistory.set(errorKey, now);
  
  // Clean up old entries
  for (const [key, timestamp] of errorHistory.entries()) {
    if (now - timestamp > DUPLICATE_THRESHOLD) {
      errorHistory.delete(key);
    }
  }
  
  return true;
}

function getErrorMessage(error: AxiosError, statusCode?: number): string {
  const responseMessage = (error.response?.data as any)?.message;
  
  // If we have a specific message from the server, use it
  if (responseMessage && typeof responseMessage === 'string') {
    return responseMessage;
  }
  
  // Fallback to generic messages based on status code
  switch (statusCode) {
    case 400:
      return 'Invalid request. Please check your input and try again.';
    case 403:
      return 'Access denied. You don\'t have permission for this action.';
    case 404:
      return 'The requested resource was not found.';
    case 408:
      return 'Request timeout. Please try again.';
    case 409:
      return 'Conflict detected. The resource may have been modified.';
    case 429:
      return 'Too many requests. Please wait a moment and try again.';
    case 500:
      return 'Server error. Our team has been notified.';
    case 502:
      return 'Service temporarily unavailable. Please try again.';
    case 503:
      return 'Service unavailable. We may be performing maintenance.';
    case 504:
      return 'Request timeout. Please try again.';
    default:
      if (statusCode && statusCode >= 500) {
        return 'Server error. Please try again later.';
      }

      return 'Network request failed. Please try again.';
  }
}

function handleNetworkError(error: AxiosError): void {
  const statusCode = error.response?.status;
  
  if (!shouldShowToast(error, statusCode)) {
    return;
  }
  
  const message = getErrorMessage(error, statusCode);
  
  // Handle different types of errors with appropriate toast types
  if (!error.response) {
    // Network errors (no response received)
    if (error.code === 'ECONNABORTED' || error.message?.includes('timeout')) {
      warnToast('⏱️ Request timeout. Please check your connection and try again.');
    } else if (error.code === 'NETWORK_ERROR' || !navigator.onLine) {
      errorToast('🌐 Network connection failed. Please check your internet connection.');
    } else {
      errorToast('🔌 Unable to connect to the server. Please try again.');
    }

    return;
  }
  
  // HTTP status code errors
  switch (statusCode) {
    case 400:
    case 404:
      warnToast(message);
      break;
    case 403:
      errorToast('🚫 ' + message);
      break;
    case 408:
    case 504:
      warnToast('⏱️ ' + message);
      break;
    case 409:
      infoToast('⚠️ ' + message);
      break;
    case 429:
      warnToast('⏳ ' + message);
      break;
    case 500:
    case 502:
    case 503:
      errorToast('🔥 ' + message);
      break;
    default:
      if (statusCode && statusCode >= 500) {
        errorToast('🚨 ' + message);
      } else {
        warnToast(message);
      }
  }
}

class AxiosClient {
  client: AxiosInstance;

  offlineClient: IOfflineGet;

  constructor() {
    this.client = this.getClient();
    this.offlineClient = offlineClient;
  }

  init() {
    const client = axios.create({
      baseURL,
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        Authorization: `Bearer ${SecureStorage.getItemSync('token')}`,
        'x-ismobile': !!reactNativeWebView,
        'x-biometric-token': SecureStorage.getItemSync('xBiometricToken') || '',
      },
    });

    client.interceptors.response.use(
      (response) => response,
      (error: AxiosError) => {
        const statusCode = error?.response?.status;
        
        // Handle authentication errors with redirect
        if (statusCode === 401) {
          SecureStorage.clear();
          persistWebViewLoadingInformation();
          
          // Instead of hard redirect, use React Router navigation
          // window.location.href = '/auth/login-all';
          
          // Show error toast for auth failure
          // errorToast('🔐 Your session has expired. Please log in again.');
          
          // You can dispatch an event or use a global state to trigger navigation
          window.dispatchEvent(new CustomEvent('auth-expired'));
        } else {
          // Show appropriate error toast for other errors
          handleNetworkError(error);
        }

        // Return a consistent error format
        const customError = {
          ...error,
          message: (error.response?.data as any)?.message || error.message,
          statusCode: statusCode,
        };

        return Promise.reject(customError);
      },
    );

    return client;
  }

  getClientToken() {
    return this.client?.defaults?.headers?.['Authorization'];
  }

  getClient(offline?: boolean) {
    const currentOffline = localStorage.getItem('netStatus') === 'offline';

    const isOffline = offline ?? currentOffline;

    const token = SecureStorage.getItemSync('token');

    if (token && isOffline) {
      if (!this.offlineClient) {
        this.offlineClient = offlineClient;
      }

      return this.offlineClient as AxiosInstance;
    }

    const storageToken = `Bearer ${token}`;

    if (!this.client || storageToken !== this.getClientToken()) {
      this.client = this.init();
    }

    return this.client;
  }
}

export const agent = new AxiosClient();
